import { Bet, Participant, Fight } from '../types';
import { addWinnings } from './wallet';
import { resolvePropBet } from './propBetResolver';

/**
 * Result processing utilities
 * Handles bet outcome calculation when fight results are entered
 */

/**
 * Process fight result and update all related bets
 * 
 * @param fight - The fight with result
 * @param bets - All bets for this fight
 * @param participants - All participants (will be updated)
 * @returns Updated participants and bets
 */
export function processFightResult(
  fight: Fight,
  bets: Bet[],
  participants: Participant[]
): {
  updatedParticipants: Participant[];
  updatedBets: Bet[];
} {
  if (!fight.result) {
    throw new Error('Fight result is required');
  }

  const fightBets = bets.filter((bet) => bet.fightId === fight.id);
  // Also get parlay bets that include this fight
  const parlayBets = bets.filter((bet) => 
    bet.isParlay && bet.parlayLegs?.some(leg => leg.fightId === fight.id)
  );
  const updatedParticipants: Participant[] = [];
  const updatedBets: Bet[] = [];

  // Process each bet
  fightBets.forEach((bet) => {
    const participant = participants.find((p) => p.id === bet.participantId);
    if (!participant) {
      return; // Skip if participant not found
    }

    let updatedBet: Bet;
    let updatedParticipant: Participant;

    // Determine bet outcome using prop bet resolver
    const result = fight.result;
    if (!result) {
      updatedBet = { ...bet, status: 'pending' };
      updatedParticipant = participant;
    } else {
      // Use prop bet resolver to determine outcome
      const resolution = resolvePropBet(bet, fight, result);
      
      if (resolution.status === 'won') {
        // Bet won - calculate payout and add winnings
        updatedBet = {
          ...bet,
          status: 'won',
          payout: resolution.payout,
        };
        
        // Add winnings to participant's winningsBalance
        updatedParticipant = addWinnings(participant, resolution.payout || 0);
      } else if (resolution.status === 'push') {
        // Push or No Contest - refund bet amount
        updatedBet = {
          ...bet,
          status: 'won', // Push counts as won for winnings balance
          payout: resolution.refund, // Refund amount
        };
        
        // Add refund to winnings balance (refund counts as winnings)
        updatedParticipant = addWinnings(participant, resolution.refund || 0);
      } else {
        // Bet lost - mark as lost (balance already deducted when bet was placed)
        updatedBet = {
          ...bet,
          status: 'lost',
        };
        
        updatedParticipant = participant; // No balance change
      }
    }

    updatedBets.push(updatedBet);

    // Update participant in array (or add if new)
    const existingIndex = updatedParticipants.findIndex((p) => p.id === participant.id);
    if (existingIndex >= 0) {
      // Update existing participant with new bet and balance
      const existing = updatedParticipants[existingIndex];
      updatedParticipants[existingIndex] = {
        ...updatedParticipant,
        bets: existing.bets.map((b) => (b.id === bet.id ? updatedBet : b)),
      };
    } else {
      // Add new participant with updated bet
      updatedParticipants.push({
        ...updatedParticipant,
        bets: participant.bets.map((b) => (b.id === bet.id ? updatedBet : b)),
      });
    }
  });

  // Process parlay bets that include this fight
  parlayBets.forEach((bet) => {
    if (!bet.parlayLegs) return;
    
    const participant = participants.find((p) => p.id === bet.participantId);
    if (!participant) return;
    
    // Update the status of the leg for this fight
    const updatedLegs = bet.parlayLegs.map(leg => {
      if (leg.fightId === fight.id) {
        // Determine if this leg won or lost
        const legBet: Bet = {
          id: `${bet.id}-leg-${leg.fightId}`,
          participantId: bet.participantId,
          fightId: leg.fightId,
          fighterId: leg.fighterId,
          marketKey: leg.marketKey,
          outcomeId: leg.outcomeId,
          outcomeName: leg.outcomeName,
          amount: 0, // Not used for leg resolution
          odds: leg.odds,
          status: 'pending',
          placedAt: bet.placedAt,
        };
        
        const resolution = resolvePropBet(legBet, fight, fight.result!);
        
        return {
          ...leg,
          status: resolution.status === 'won' ? 'won' as const : 
                  resolution.status === 'push' ? 'won' as const : 'lost' as const,
        };
      }
      return leg;
    });
    
    // Determine overall parlay status
    const hasLost = updatedLegs.some(leg => leg.status === 'lost');
    const allWon = updatedLegs.every(leg => leg.status === 'won');
    
    let parlayStatus: 'pending' | 'won' | 'lost' = 'pending';
    let parlayPayout: number | undefined;
    
    if (hasLost) {
      // If ANY leg loses, entire parlay loses
      parlayStatus = 'lost';
    } else if (allWon) {
      // If ALL legs win, parlay wins
      parlayStatus = 'won';
      // Calculate payout using parlay odds
      const decimalOdds = bet.odds >= 0 
        ? (bet.odds / 100) + 1 
        : (100 / Math.abs(bet.odds)) + 1;
      parlayPayout = bet.amount * decimalOdds;
    }
    // Otherwise, status remains 'pending'
    
    const updatedBet: Bet = {
      ...bet,
      parlayLegs: updatedLegs,
      status: parlayStatus,
      payout: parlayPayout,
    };
    
    updatedBets.push(updatedBet);
    
    // Update participant if parlay won
    let updatedParticipant = participant;
    if (parlayStatus === 'won' && parlayPayout) {
      updatedParticipant = addWinnings(participant, parlayPayout);
    }
    
    // Update participant in array
    const existingIndex = updatedParticipants.findIndex((p) => p.id === participant.id);
    if (existingIndex >= 0) {
      const existing = updatedParticipants[existingIndex];
      updatedParticipants[existingIndex] = {
        ...updatedParticipant,
        bets: existing.bets.map((b) => (b.id === bet.id ? updatedBet : b)),
      };
    } else {
      updatedParticipants.push({
        ...updatedParticipant,
        bets: participant.bets.map((b) => (b.id === bet.id ? updatedBet : b)),
      });
    }
  });

  // Add participants that weren't affected (no bets on this fight)
  participants.forEach((participant) => {
    const hasBet = fightBets.some((bet) => bet.participantId === participant.id);
    const hasParlay = parlayBets.some((bet) => bet.participantId === participant.id);
    if (!hasBet && !hasParlay) {
      const existingIndex = updatedParticipants.findIndex((p) => p.id === participant.id);
      if (existingIndex < 0) {
        updatedParticipants.push(participant);
      }
    }
  });

  // Add bets that weren't affected (not for this fight and not parlays with this fight)
  bets.forEach((bet) => {
    const isAffected = bet.fightId === fight.id || 
                      (bet.isParlay && bet.parlayLegs?.some(leg => leg.fightId === fight.id));
    if (!isAffected) {
      updatedBets.push(bet);
    }
  });

  return {
    updatedParticipants,
    updatedBets,
  };
}

/**
 * Get bet outcome summary for a fight
 * 
 * @param fight - The fight with result
 * @param bets - All bets for this fight
 * @returns Summary of bet outcomes
 */
export function getBetOutcomeSummary(
  fight: Fight,
  bets: Bet[]
): {
  totalBets: number;
  winningBets: number;
  losingBets: number;
  totalPayouts: number;
} {
  if (!fight.result) {
    return {
      totalBets: 0,
      winningBets: 0,
      losingBets: 0,
      totalPayouts: 0,
    };
  }

  const fightBets = bets.filter((bet) => bet.fightId === fight.id);
  let winningBets = 0;
  let losingBets = 0;
  let totalPayouts = 0;

  fightBets.forEach((bet) => {
    const resolution = resolvePropBet(bet, fight, fight.result!);
    if (resolution.status === 'won') {
      winningBets++;
      if (resolution.payout) {
        totalPayouts += resolution.payout;
      }
    } else if (resolution.status === 'push') {
      // Push counts as win for summary
      winningBets++;
      if (resolution.refund) {
        totalPayouts += resolution.refund;
      }
    } else {
      losingBets++;
    }
  });

  return {
    totalBets: fightBets.length,
    winningBets,
    losingBets,
    totalPayouts,
  };
}

